import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, MessageSquare, Share2, MoreHorizontal, ChevronLeft } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';

export default function ReelPage() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [videoLoading, setVideoLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [videoError, setVideoError] = useState(null);
  const [reels, setReels] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const videoRefs = useRef([]);

  // Fetch reels for the store
  useEffect(() => {
    // In your fetchReels function:
    const fetchReels = async () => {
    try {
        setLoading(true);
        const params = { 
        page: 1, 
        limit: 10,
        ...(storeId && { storeId }) 
        };
        
        const { data } = await api.get('/reels', { params });
        setReels(data.data.reels || []); // Changed from data.data.docs to data.data.reels
    } catch (err) {
        console.error('Error fetching reels:', err);
        setError('Failed to load reels');
    } finally {
        setLoading(false);
    }
    };

    fetchReels();
  }, [storeId]);

  // Handle video play/pause when changing reels
    useEffect(() => {
    const playVideo = async () => {
        if (videoRefs.current[currentIndex]) {
        try {
            // Ensure the video is muted for autoplay to work in most browsers
            videoRefs.current[currentIndex].muted = true;
            await videoRefs.current[currentIndex].play();
        } catch (err) {
            console.error('Error playing video:', err);
            // Fallback for browsers that block autoplay
            videoRefs.current[currentIndex].controls = true;
        }
        }
    };

    playVideo();

    return () => {
        videoRefs.current.forEach(video => {
        if (video) {
            video.pause();
            video.currentTime = 0;
        }
        });
    };
    }, [currentIndex, reels]); // Added reels to dependency array

  const handleSwipe = (direction) => {
    if (direction === 'up' && currentIndex < reels.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else if (direction === 'down' && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleLike = async (reelId) => {
    try {
      const { data } = await api.post(`/reels/${reelId}/like`);
      
      setReels(prev => prev.map(reel => 
        reel._id === reelId 
          ? { ...reel, likes: data.data.likes, likeCount: data.data.likes.length } 
          : reel
      ));
    } catch (err) {
      console.error('Error liking reel:', err);
    }
  };

  const handleCommentSubmit = async (reelId, text) => {
    try {
      const { data } = await api.post(`/reels/${reelId}/comments`, { text });
      
      setReels(prev => prev.map(reel => 
        reel._id === reelId 
          ? { 
              ...reel, 
              comments: [...reel.comments, data.data.comment],
              commentCount: reel.commentCount + 1
            } 
          : reel
      ));
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  };

  const handleShare = async (reelId) => {
    try {
      const shareUrl = `${window.location.origin}/reels/${reelId}`;
      
      if (navigator.share) {
        await navigator.share({
          title: 'Check out this reel',
          url: shareUrl
        });
      } else {
        // Fallback for browsers that don't support Web Share API
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-red-500 text-lg">{error}</p>
      </div>
    );
  }

  if (reels.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-center p-4">
        <div className="bg-gray-100 p-6 rounded-full mb-4">
          <svg 
            className="w-12 h-12 text-gray-400" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="1.5" 
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">No Reels Found</h3>
        <p className="text-gray-600 max-w-md">
          {storeId 
            ? "This store hasn't posted any reels yet." 
            : "No reels available. Check back later!"}
        </p>
      </div>
    );
  }

  const currentReel = reels[currentIndex];
  const isLiked = user && currentReel.likes.some(like => like._id === user._id);

  return (
    <div className="relative h-screen bg-black overflow-hidden">
      {/* Back button */}
      <button 
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full"
      >
        <ChevronLeft size={24} />
      </button>

      {/* Reel container */}
      <div 
        className="h-full w-full flex flex-col items-center justify-center"
        onTouchStart={e => {
          const startY = e.touches[0].clientY;
          const handleTouchEnd = (e) => {
            const endY = e.changedTouches[0].clientY;
            const diff = startY - endY;
            if (Math.abs(diff) > 50) {
              handleSwipe(diff > 0 ? 'up' : 'down');
            }
            document.removeEventListener('touchend', handleTouchEnd);
          };
          document.addEventListener('touchend', handleTouchEnd);
        }}
      >
        {/* Video */}
        <div className="relative w-full h-full max-w-md mx-auto">
        <video
        ref={el => videoRefs.current[currentIndex] = el}
        src={currentReel.video.url}
        loop
        playsInline
        className="w-full h-full object-cover"
        onWaiting={() => setVideoLoading(true)}
        onCanPlay={() => setVideoLoading(false)}
        onError={() => {
            setVideoError('Failed to load video');
            setVideoLoading(false);
        }}
        onClick={() => {
            const video = videoRefs.current[currentIndex];
            if (video.paused) {
            video.play().catch(err => {
                console.error('Error playing video:', err);
                video.controls = true;
            });
            } else {
            video.pause();
            }
        }}
        />


          {/* Video overlay controls */}
          <div className="absolute inset-0 flex flex-col justify-between p-4">
            {/* Top bar - store info */}
            <div className="flex items-center justify-between">
              <div 
                className="flex items-center space-x-2 cursor-pointer"
                onClick={() => navigate(`/store/${currentReel.store.slug}`)}
              >
                <img 
                  src={currentReel.store.logo || '/default-store.png'} 
                  alt={currentReel.store.name}
                  className="w-8 h-8 rounded-full border-2 border-white"
                />
                <span className="text-white font-semibold text-sm">
                  {currentReel.store.name}
                </span>
              </div>
              
              <button className="text-white bg-black bg-opacity-30 p-1 rounded-full">
                <MoreHorizontal size={20} />
              </button>
            </div>

            {/* Right sidebar - actions */}
            <div className="absolute right-4 bottom-20 flex flex-col items-center space-y-5">
              {/* Like button */}
              <div className="flex flex-col items-center">
                <button 
                  onClick={() => handleLike(currentReel._id)}
                  className="p-2 bg-black bg-opacity-30 rounded-full"
                >
                  <Heart 
                    size={28} 
                    className={isLiked ? 'fill-red-500 text-red-500' : 'text-white'} 
                  />
                </button>
                <span className="text-white text-xs mt-1">
                  {currentReel.likeCount}
                </span>
              </div>

              {/* Comment button */}
              <div className="flex flex-col items-center">
                <button 
                  onClick={() => navigate(`/reels/${currentReel._id}/comments`)}
                  className="p-2 bg-black bg-opacity-30 rounded-full"
                >
                  <MessageSquare size={28} className="text-white" />
                </button>
                <span className="text-white text-xs mt-1">
                  {currentReel.commentCount}
                </span>
              </div>

              {/* Share button */}
              <div className="flex flex-col items-center">
                <button 
                  onClick={() => handleShare(currentReel._id)}
                  className="p-2 bg-black bg-opacity-30 rounded-full"
                >
                  <Share2 size={28} className="text-white" />
                </button>
              </div>
            </div>
            <button 
                onClick={() => {
                    const video = videoRefs.current[currentIndex];
                    if (video) {
                    video.muted = !video.muted;
                    setIsMuted(video.muted);
                    }
                }}
                className="absolute left-4 bottom-20 bg-black bg-opacity-30 p-2 rounded-full"
                >
                {isMuted ? (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clipRule="evenodd" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                ) : (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 010 7.072M12 6a7.975 7.975 0 015.657 2.343m0 0a7.975 7.975 0 010 11.314m-11.314 0a7.975 7.975 0 010-11.314m0 0a7.975 7.975 0 015.657-2.343" />
                    </svg>
                )}
                </button>

            {/* Bottom bar - caption and product */}
            <div className="max-w-[70%]">
              <p className="text-white font-semibold text-sm mb-2 line-clamp-2">
                {currentReel.title}
              </p>
              <p className="text-white text-xs mb-3 line-clamp-2">
                {currentReel.description}
              </p>

              {currentReel.product && (
                <div 
                  className="flex items-center space-x-2 bg-black bg-opacity-30 p-2 rounded-lg cursor-pointer"
                  onClick={() => navigate(`/store/${currentReel.store.slug}/products/${currentReel.product._id}`)}
                >
                  <img 
                    src={currentReel.product.design?.image || '/default-product.jpg'} 
                    alt={currentReel.product.name}
                    className="w-10 h-10 rounded object-cover"
                  />
                  <div>
                    <p className="text-white text-xs font-medium">
                      {currentReel.product.name}
                    </p>
                    <p className="text-white text-xs">
                      ₹{currentReel.product.variants?.[0]?.finalPrice || 'N/A'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation dots for desktop */}
      {reels.length > 1 && (
        <div className="hidden md:flex fixed right-4 top-1/2 transform -translate-y-1/2 flex-col space-y-2">
          {reels.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full ${index === currentIndex ? 'bg-white' : 'bg-gray-500'}`}
            />
          ))}
        </div>
      )}
      {videoError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70">
            <p className="text-white text-lg">{videoError}</p>
        </div>
       )}
       {videoLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner />
        </div>
        )}
    </div>
  );
}